/**
 * React Query hooks for the per-user UI preferences store. See
 * `@/types/userPreferences` for the boundary rules (this is NOT the
 * membership-preferences store, and columns are NOT a preference here).
 */
import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  fetchUserPreferences,
  upsertUserPreference,
} from '@/api/userPreferences';
import { logger } from '@/lib/logger';
import type { UserPreferenceRow } from '@/types/userPreferences';

const USER_PREFERENCES_STALE_MS = 5 * 60 * 1000;

export const USER_PREFERENCES_QUERY_KEY = (orgId: number) =>
  ['userPreferences', orgId] as const;

/** Read the cached row matching (key, scope), or undefined if absent. */
function findRow(
  rows: UserPreferenceRow[] | undefined,
  key: string,
  scope: string
): UserPreferenceRow | undefined {
  return rows?.find(row => row.key === key && row.scope === scope);
}

/** Replace (or remove, when `row` is undefined) the (key, scope) entry in the cached array. */
function withRow(
  rows: UserPreferenceRow[] | undefined,
  key: string,
  scope: string,
  row: UserPreferenceRow | undefined
): UserPreferenceRow[] {
  const withoutRow = (rows ?? []).filter(
    r => !(r.key === key && r.scope === scope)
  );
  return row ? [...withoutRow, row] : withoutRow;
}

/** Shared onMutate: cancel in-flight fetches and snapshot only the affected row. */
async function snapshotRow(
  queryClient: QueryClient,
  queryKey: ReturnType<typeof USER_PREFERENCES_QUERY_KEY>,
  key: string,
  scope: string
): Promise<UserPreferenceRow | undefined> {
  await queryClient.cancelQueries({ queryKey });
  const rows = queryClient.getQueryData<UserPreferenceRow[]>(queryKey);
  return findRow(rows, key, scope);
}

/** Shared onError: restore just the affected row (never a whole-list snapshot). */
function restoreRow(
  queryClient: QueryClient,
  queryKey: ReturnType<typeof USER_PREFERENCES_QUERY_KEY>,
  key: string,
  scope: string,
  previousRow: UserPreferenceRow | undefined
): void {
  queryClient.setQueryData<UserPreferenceRow[]>(queryKey, rows =>
    withRow(rows, key, scope, previousRow)
  );
}

export interface UseUserPreferencesReturn {
  /** Read a preference's value by (key, scope). Stable across renders that don't change the underlying data. */
  getPreference: <T = unknown>(key: string, scope: string) => T | undefined;
  isSuccess: boolean;
  isError: boolean;
}

/**
 * Fetches all of the current user's UI preferences for an org in one bulk
 * query. Callers must gate writes on `isSuccess`/`isError` — writing before
 * the initial fetch resolves risks clobbering rows the client hasn't seen
 * yet on the next `invalidateQueries` refetch.
 */
export function useUserPreferences(orgId: number): UseUserPreferencesReturn {
  const { data, isSuccess, isError } = useQuery({
    queryKey: USER_PREFERENCES_QUERY_KEY(orgId),
    queryFn: ({ signal }) => fetchUserPreferences(orgId, signal),
    staleTime: USER_PREFERENCES_STALE_MS,
  });

  const getPreference = useCallback(
    <T = unknown>(key: string, scope: string): T | undefined =>
      findRow(data, key, scope)?.value as T | undefined,
    [data]
  );

  return { getPreference, isSuccess, isError };
}

interface SetPreferenceVariables {
  key: string;
  scope: string;
  value: unknown;
}

/**
 * Sets (upserts) one preference row, optimistically. On failure the
 * affected row is rolled back to its pre-mutation value and a warning is
 * logged; the caller is responsible for user-visible failure feedback.
 */
export function useSetUserPreference(orgId: number) {
  const queryClient = useQueryClient();
  const queryKey = USER_PREFERENCES_QUERY_KEY(orgId);

  return useMutation({
    mutationFn: ({ key, scope, value }: SetPreferenceVariables) =>
      upsertUserPreference(orgId, { key, scope, value }),
    onMutate: async ({ key, scope, value }: SetPreferenceVariables) => {
      const previousRow = await snapshotRow(queryClient, queryKey, key, scope);
      queryClient.setQueryData<UserPreferenceRow[]>(queryKey, rows =>
        withRow(rows, key, scope, {
          key,
          scope,
          value,
          updated_at: new Date().toISOString(),
        })
      );
      return { previousRow, key, scope };
    },
    onError: (error, variables, context) => {
      if (context) {
        restoreRow(
          queryClient,
          queryKey,
          context.key,
          context.scope,
          context.previousRow
        );
      }
      logger.warn('Failed to set user preference', {
        component: 'user-preferences',
        org_id: orgId,
        key: variables.key,
        scope: variables.scope,
        error_type: error instanceof Error ? error.name : undefined,
        error_message: error instanceof Error ? error.message : String(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

interface ClearPreferenceVariables {
  key: string;
  scope: string;
}

/**
 * Clears one preference row via `value: null` (there is no delete
 * endpoint), optimistically removing it from the cache. On failure the
 * affected row is restored and a warning is logged; the caller is
 * responsible for user-visible failure feedback.
 */
export function useClearUserPreference(orgId: number) {
  const queryClient = useQueryClient();
  const queryKey = USER_PREFERENCES_QUERY_KEY(orgId);

  return useMutation({
    mutationFn: ({ key, scope }: ClearPreferenceVariables) =>
      upsertUserPreference(orgId, { key, scope, value: null }),
    onMutate: async ({ key, scope }: ClearPreferenceVariables) => {
      const previousRow = await snapshotRow(queryClient, queryKey, key, scope);
      queryClient.setQueryData<UserPreferenceRow[]>(queryKey, rows =>
        withRow(rows, key, scope, undefined)
      );
      return { previousRow, key, scope };
    },
    onError: (error, variables, context) => {
      if (context) {
        restoreRow(
          queryClient,
          queryKey,
          context.key,
          context.scope,
          context.previousRow
        );
      }
      logger.warn('Failed to clear user preference', {
        component: 'user-preferences',
        org_id: orgId,
        key: variables.key,
        scope: variables.scope,
        error_type: error instanceof Error ? error.name : undefined,
        error_message: error instanceof Error ? error.message : String(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
